import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LayoutGrid, Plus } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@bookdental/ui";
import { cn } from "@/lib/cn";
import type { AcaoDeCriar } from "./acoes-de-criar";

/** Acima disso a coluna fica alta demais pra uma mão: mostra as primeiras e um "Ver tudo". */
const MAXIMO_NA_COLUNA = 6;
const DURACAO = 180;

type Props = {
  aberto: boolean;
  onFechar: () => void;
  /** Onde está o "+" na tela — a coluna nasce dele e o "×" ocupa o mesmo lugar. */
  origem: DOMRect | null;
  acoes: AcaoDeCriar[];
};

/**
 * O "+" da barra de abas: a tela escurece, o "+" gira e vira "×" e as ações sobem em coluna acima
 * dele, uma depois da outra — círculo colorido com o nome ao lado. Com mais de seis ações a coluna
 * mostra cinco e "Ver tudo", que abre a lista completa.
 */
export function MenuCriar({ aberto, onFechar, origem, acoes }: Props) {
  const [montado, setMontado] = useState(false);
  const [visivel, setVisivel] = useState(false);
  const [verTudo, setVerTudo] = useState(false);
  const primeiraRef = useRef<HTMLButtonElement>(null);

  // Montar invisível e só então ligar a transição; ao fechar, esperar a saída antes de desmontar.
  useEffect(() => {
    if (aberto) {
      setMontado(true);
      const id = requestAnimationFrame(() => requestAnimationFrame(() => setVisivel(true)));
      return () => cancelAnimationFrame(id);
    }
    setVisivel(false);
    const t = setTimeout(() => setMontado(false), DURACAO);
    return () => clearTimeout(t);
  }, [aberto]);

  useEffect(() => {
    if (!visivel) return;
    primeiraRef.current?.focus();
    const tecla = (e: KeyboardEvent) => e.key === "Escape" && onFechar();
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, [visivel, onFechar]);

  const escolher = (acao: () => void) => {
    onFechar();
    setTimeout(acao, DURACAO);
  };

  const cabem = acoes.length <= MAXIMO_NA_COLUNA;
  const naColuna: AcaoDeCriar[] = cabem
    ? acoes
    : [...acoes.slice(0, MAXIMO_NA_COLUNA - 1), { chave: "tudo", rotulo: "Ver tudo", icone: LayoutGrid, cor: "bg-zinc-700", executar: () => setVerTudo(true) }];

  const centroX = origem ? origem.left + origem.width / 2 : window.innerWidth / 2;
  const base = origem ? window.innerHeight - origem.top + 12 : 96;

  return (
    <>
      {montado &&
        createPortal(
          <div role="dialog" aria-modal="true" aria-label="Criar" className="fixed inset-0 z-50 md:hidden">
            <button
              type="button"
              aria-label="Fechar"
              tabIndex={-1}
              onClick={onFechar}
              className={cn(
                "absolute inset-0 bg-zinc-950/55 backdrop-blur-[2px] transition-opacity",
                visivel ? "opacity-100" : "opacity-0",
              )}
              style={{ transitionDuration: `${DURACAO}ms` }}
            />
            {/* Cada ação é um cartão branco único (círculo + nome juntos), todos da mesma largura e
                centrados no eixo do "+" — separados, nome e ícone pareciam duas coisas. */}
            <ul
              className="absolute flex w-[min(19rem,calc(100vw-2rem))] -translate-x-1/2 flex-col-reverse gap-2.5"
              style={{ left: centroX, bottom: base }}
            >
              {naColuna.map((a, i) => (
                <li
                  key={a.chave}
                  className={cn("transition-all ease-out", visivel ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0")}
                  style={{ transitionDuration: `${DURACAO}ms`, transitionDelay: visivel ? `${i * 45}ms` : "0ms" }}
                >
                  <button
                    ref={i === 0 ? primeiraRef : undefined}
                    type="button"
                    disabled={a.desabilitada}
                    onClick={() => escolher(a.executar)}
                    className="flex h-16 w-full items-center gap-3.5 rounded-2xl border border-zinc-200 bg-white px-2.5 text-left shadow-xl transition-transform active:scale-[0.98] disabled:opacity-60 dark:border-border dark:bg-card"
                  >
                    <span className={cn("flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-white", a.cor)}>
                      <a.icone className="h-[22px] w-[22px]" />
                    </span>
                    <span className="text-base font-semibold text-zinc-900 dark:text-foreground">{a.rotulo}</span>
                  </button>
                </li>
              ))}
            </ul>
            {origem && (
              <button
                type="button"
                aria-label="Fechar"
                onClick={onFechar}
                className="absolute flex items-center justify-center"
                style={{ left: origem.left, top: origem.top, width: origem.width, height: origem.height }}
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-600 text-white shadow-lg">
                  <Plus
                    className={cn("h-6 w-6 transition-transform", visivel ? "rotate-45" : "rotate-0")}
                    style={{ transitionDuration: `${DURACAO}ms` }}
                  />
                </span>
              </button>
            )}
          </div>,
          document.body,
        )}

      <Sheet open={verTudo} onOpenChange={setVerTudo}>
        <SheetContent
          side="bottom"
          className="rounded-t-2xl border-zinc-200 bg-white p-4 dark:border-border dark:bg-card"
          style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
        >
          <SheetTitle className="mb-3 text-base font-semibold">Criar</SheetTitle>
          <div className="grid grid-cols-3 gap-3">
            {acoes.map((a) => (
              <button
                key={a.chave}
                type="button"
                disabled={a.desabilitada}
                onClick={() => {
                  setVerTudo(false);
                  setTimeout(a.executar, 250);
                }}
                className="flex flex-col items-center gap-1.5 rounded-xl p-2 text-center text-xs font-medium text-foreground active:bg-zinc-100 disabled:opacity-60 dark:active:bg-muted"
              >
                <span className={cn("flex h-12 w-12 items-center justify-center rounded-full text-white", a.cor)}>
                  <a.icone className="h-5 w-5" />
                </span>
                {a.rotulo}
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
